// Definizioni statiche delle tab fornitore della pagina Fornitori unificata
// (decisione Davide 2026-07-03: una tab per fornitore, dentro TUTTO del
// fornitore — anagrafica, strumenti, catalogo dimensioni, prodotti Poleepo).
//
// ``id`` = supplier_id del catalogo backend (GET /api/catalog/suppliers).
// ``brandName`` = chiave di match (case-insensitive contains) contro i brand
// Poleepo di GET /api/catalog/brands: se il brand non esiste la sezione
// prodotti si nasconde senza errori.

/** Flag dell'ordine che attribuisce l'ordine a questo fornitore quando il
 *  catalogo non basta: WebFurniture = ``wf_eligible``, Magazzino = SKU a stock
 *  BS COM. Gli altri fornitori non ne hanno bisogno — dal 2026-09-04 il match
 *  e' per id contro kind + catalogo PIM (``fornitoreFiltro.ts``), quindi ogni
 *  tab e' filtrabile, Bizzotto compreso. */
export type SupplierOrderFlag = "wfEligible" | "inWarehouse";

export interface SupplierTabDef {
  id: string;
  label: string;
  brandName: string;
  nome: string;
  /** Email ordini/contatto; null per chi non ha un indirizzo di riferimento. */
  email: string | null;
  /** Come funziona il flusso: 2-4 righe informative statiche. */
  flusso: string[];
  /** Flag d'ordine per il filtro fornitore della pagina Ordini (vedi sopra). */
  orderFlag?: SupplierOrderFlag;
  /** Magazzino della pagina Ritiri da cui parte il pacco (``pickups.magazzini``);
   *  null = il fornitore non ha un ritiro SDA prenotato da OM. */
  ritiro: string | null;
  /** Solo con ``ritiro`` null: perché. */
  ritiroNota?: string;
  /** Chi condivide lo stesso magazzino: il suo interruttore è anche il loro. */
  ritiroCondiviso?: string;
}

export const SUPPLIER_TABS: readonly SupplierTabDef[] = [
  {
    id: "itamoby",
    label: "ITAMOBY",
    brandName: "ITAMOBY",
    nome: "Itamoby",
    email: "info@itamoby.com",
    flusso: [
      "Ordini inviati via WooCommerce (portale fornitore).",
      "Conferme e merce-pronta arrivano via mail.",
      "Pagamento con bonifico anticipato.",
      "Pickup: mail con LDV allegata via Thunderbird/SMTP.",
    ],
    ritiro: "itamoby",
    ritiroCondiviso: "Ityhome",
  },
  {
    id: "ityhome",
    label: "ITYHOME",
    brandName: "ITYHOME",
    nome: "Ityhome",
    email: "info@ityhome.com",
    flusso: [
      "Ordini inviati via WooCommerce (portale fornitore).",
      "Conferme e merce-pronta arrivano via mail.",
      "Pagamento con bonifico anticipato.",
      "Pickup: mail con LDV allegata via Thunderbird/SMTP.",
    ],
    ritiro: "itamoby",
    ritiroCondiviso: "Itamoby",
  },
  {
    id: "cosma",
    label: "COSMA",
    brandName: "COSMA",
    nome: "Cosma",
    email: null,
    flusso: [
      "Invio ordini via FTP: LDV in /Ldv/, CSV ordine in /Ordini/.",
      "Conferma del numero ordine via mail.",
      "MAI contrassegno: i campi incasso restano sempre vuoti.",
    ],
    ritiro: "cosma",
  },
  {
    id: "allservice",
    label: "ALL SERVICE",
    brandName: "ALLSERVICE",
    nome: "All Service",
    email: "biancade.ufficio@allservice.tv",
    flusso: [
      "Ordini via mail con template XLSX.",
      "Tracking giornaliero importato da XLSX.",
      "Giacenze da XLSX (foglio Codici_Entrate_Saldo).",
    ],
    ritiro: null,
    ritiroNota: "All Service spedisce coi suoi flussi: nessun ritiro SDA da prenotare in OM.",
  },
  {
    id: "web_furniture",
    label: "WEB FURNITURE",
    brandName: "WEB",
    nome: "Web Furniture",
    email: null,
    flusso: [
      "Ordini via mail: 1 mail per ordine con LDV allegata.",
      "Catalogo da Google Sheet (498 SKU gsheet).",
      "Tasti azione gatati da wf_eligible.",
    ],
    orderFlag: "wfEligible",
    ritiro: "webfurniture",
  },
  {
    id: "bizzotto",
    label: "BIZZOTTO",
    brandName: "BIZZOTTO",
    nome: "Bizzotto",
    email: "noreply@bizzotto.com",
    flusso: [
      "Ordine dal portale my.bizzotto.com, guidato da un browser headless: carrello, nome carrello = nostra reference, nota col cliente, conferma.",
      "La merce parte dal magazzino Bizzotto di Arquà Polesine con la nostra LDV SDA: il corriere la ritira lì.",
      "Conferme ordine via mail: il classifier estrae N.Ord.Forn e reference.",
      "Catalogo importato dal Google Sheet di Davide.",
    ],
    // Nessun orderFlag: il tasto «Ordina» si accende dal catalogo tramite
    // ``portaleFornitore``, come Ityhome e Itamoby.
    ritiro: "bizzotto",
  },
  {
    id: "aurora",
    label: "AURORA BEDDING",
    // Nessun brand Poleepo: la sezione prodotti si nasconde da sola.
    brandName: "AURORA",
    nome: "Aurora Bedding",
    email: "info@aurorabedding.eu",
    flusso: [
      "Ordini via mail: 1 mail per ordine con LDV allegata, come Web Furniture.",
      "Anagrafica prodotti e colli dal PIM (7 SKU MAPL, nessun catalogo importato in OM).",
      "Il tasto «Ordina al fornitore» si accende dal PIM (campo fornitore_mail).",
    ],
    // Nessun orderFlag: l'appartenenza si legge dal PIM (``fornitoreFiltro``).
    ritiro: "aurora",
  },
  {
    id: "magazzino",
    label: "MAGAZZINO",
    // Nessun brand Poleepo associato: la sezione prodotti si nasconde da sola.
    brandName: "MAGAZZINO-BSCOM",
    nome: "Magazzino BS COM",
    email: null,
    flusso: [
      "Fornitore interno: merce fisicamente a stock da noi.",
      "Sincronizzato in automatico (15 min) dall'app MATECPRO Logistics.",
      "Sono nostri i prodotti MATECPRO con azienda BS COM.",
      "Il pezzo a stock vince: va all'ordine piu' vecchio, e quell'ordine non va al fornitore.",
      "Card con striscia teal; dopo la LDV il ritiro si prenota nella scheda BS COM.",
    ],
    orderFlag: "inWarehouse",
    ritiro: "bscom",
  },
];
